import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Mail, Lock } from 'lucide-react';

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const hasTracked = useRef(false);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Track page visit for analytics (with guard against StrictMode double-fire)
  useEffect(() => {
    if (hasTracked.current) return;
    hasTracked.current = true;
    
    const trackVisit = async () => {
      try {
        await fetch(`${API_URL}/api/analytics/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page: 'login',
            referrer: document.referrer || null,
            user_agent: navigator.userAgent
          })
        });
      } catch (err) {
        // Silently fail - analytics shouldn't break the app
        console.debug('Analytics tracking failed:', err);
      }
    };
    trackVisit();
  }, [API_URL]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success('Welcome back!');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-darker flex flex-col">
      {/* Header */}
      <div className="flex-1 flex flex-col items-center justify-center p-6">
        <img 
          src="/favicon.png" 
          alt="MyJobTools" 
          className="w-20 h-20 mb-3"
        />
        <h1 className="font-heading text-3xl font-bold italic mb-2">
          <span className="text-slate-200">MyJob</span>
          <span className="text-[#FF5F00]">Tools</span>
        </h1>
        <p className="text-slate-400 text-center mb-8">
          The toolbox for your paperwork.
        </p>
        
        {/* Login Form */}
        <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-slate-300 uppercase text-xs tracking-wider font-bold">
              Email
            </Label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-14 pl-12 bg-slate-dark border-2 border-slate-light text-white placeholder:text-slate-500 text-lg rounded-lg"
                required
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="password" className="text-slate-300 uppercase text-xs tracking-wider font-bold">
              Password
            </Label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="h-14 pl-12 bg-slate-dark border-2 border-slate-light text-white placeholder:text-slate-500 text-lg rounded-lg"
                required
              />
            </div>
          </div>
          
          <Button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="w-full h-14 bg-[#FF5F00] hover:bg-orange-hover text-white font-bold uppercase tracking-wide text-lg rounded-lg shadow-hard active:translate-y-0.5 transition-transform"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
        
        <p className="mt-6 text-slate-400">
          Don't have an account?{' '}
          <Link to="/register" className="text-[#FF5F00] font-bold hover:underline" data-testid="register-link">
            Sign Up
          </Link>
        </p>
      </div>
      
      {/* Footer tagline */}
      <div className="p-6 text-center">
        <p className="text-slate-500 text-sm">
          Month-to-month. Cancel with one tap.
        </p>
      </div>
    </div>
  );
}
